const BASE = process.env.REACT_APP_BACKEND_URL || '';

export class ApiError extends Error {
  constructor(status, detail, message) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return JSON.stringify(detail);
}

function safeJson(t) {
  try { return JSON.parse(t); } catch { return null; }
}

function isHtml(text) {
  return typeof text === 'string' && (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html'));
}

export async function apiFetch(path, opts = {}) {
  const url = `${BASE}/api${path}`;
  const headers = { "Content-Type": "application/json", Accept: "application/json" };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? 60000);
  let res;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e.name === 'AbortError') throw new ApiError(0, null, 'Request timed out. Please try again.');
    throw new ApiError(0, null, e.message || 'Network error');
  } finally { clearTimeout(timer); }
  const text = await res.text();
  // Detect HTML error pages (e.g. Cloudflare 502/504)
  if (!res.ok && isHtml(text)) {
    const statusMsg = res.status === 502 ? 'Server is still processing — please wait.'
      : res.status === 504 ? 'Gateway timeout — the server is busy.'
      : `Server error (${res.status})`;
    throw new ApiError(res.status, null, statusMsg);
  }
  const data = text ? safeJson(text) ?? text : null;
  if (!res.ok) {
    const detail = (data && typeof data === 'object' && data.detail) ?? (typeof data === 'string' ? data : null) ?? res.statusText;
    const structured = (detail && typeof detail === "object" && detail.code) ? detail : null;
    const msg = formatDetail(structured ? (structured.detail || structured.code) : detail);
    throw new ApiError(res.status, structured || data, msg);
  }
  return data;
}

/**
 * Fire-and-poll refresh: sends the POST (with a short race timeout) then
 * polls the GET endpoint until generated_at changes or maxWaitMs elapses.
 * Works around Cloudflare's 60s proxy timeout and long LLM calls.
 */
export async function pollRefresh({ postFn, getFn, prevGeneratedAt, pollIntervalMs = 5000, maxWaitMs = 120000 }) {
  // Fire POST with a short race — if it returns fast, use the response directly.
  // If not, the POST continues in the background and we poll GET.
  const POST_RACE_MS = 12000;
  // Keep a handle on the POST promise so we can:
  //  a) catch its eventual rejection (prevents unhandled-rejection on 502/timeout)
  //  b) check its result during polling
  let postResult = null;
  let postDone = false;
  const postPromise = postFn()
    .then(r => { postResult = r; postDone = true; return r; })
    .catch(e => { postDone = true; /* swallow — POST may 502 behind proxy */ });

  try {
    const raceTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('POST_RACE_TIMEOUT')), POST_RACE_MS));
    const result = await Promise.race([postPromise, raceTimeout]);
    // If POST returned fresh data within the race window, use it
    if (result && result.generated_at && result.generated_at !== prevGeneratedAt) {
      return result;
    }
  } catch (e) {
    // Expected: POST took longer than race window
    console.log('[pollRefresh] POST race done (expected):', e?.message?.slice(0, 60));
  }
  // Poll GET until fresh data appears. Also check the in-flight POST result each tick.
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, pollIntervalMs));
    // Check if POST finished in the background with fresh data
    if (postDone && postResult?.generated_at && postResult.generated_at !== prevGeneratedAt) {
      return postResult;
    }
    try {
      const data = await getFn();
      if (data && data.generated_at && data.generated_at !== prevGeneratedAt) {
        return data;
      }
    } catch (e) {
      console.log('[pollRefresh] GET poll error:', e?.message?.slice(0, 80));
    }
  }
  throw new ApiError(0, null, 'Refresh is taking too long. Please try again in a minute.');
}

export const api = {
  // Auth
  login: (b) => apiFetch("/auth/login", { method: "POST", body: b }),
  signup: (b) => apiFetch("/auth/signup", { method: "POST", body: b }),
  signupWithInvite: (b) => apiFetch("/auth/signup-with-invite", { method: "POST", body: b }),
  me: (t) => apiFetch("/auth/me", { token: t }),
  states: () => apiFetch("/meta/states"),

  // Business health
  businessHealth: (t) => apiFetch("/business-health", { token: t }),

  // AI
  aiMorningBriefing: (t) => apiFetch("/ai/morning-briefing", { token: t }),
  aiChat: (t, body) => apiFetch("/ai/chat", { method: "POST", body, token: t, timeoutMs: 90000 }),
  aiHistory: (t, sid) => apiFetch(`/ai/chat/history?session_id=${encodeURIComponent(sid)}`, { token: t }),
  aiPending: (t) => apiFetch("/ai/actions/pending", { token: t }),
  aiApprove: (t, id) => apiFetch(`/ai/actions/${id}/approve`, { method: "POST", token: t }),
  aiReject: (t, id) => apiFetch(`/ai/actions/${id}/reject`, { method: "POST", token: t }),

  // Manager analysis
  managerAnalysis: (t, slug, refresh = false) => apiFetch(`/managers/${slug}/analysis${refresh ? "?refresh=1" : ""}`, { token: t }),
  managerRefresh: (t, slug) => apiFetch(`/managers/${slug}/refresh`, { method: "POST", token: t, timeoutMs: 180000 }),

  // Action Engine
  proposeAction: (t, body) => apiFetch("/actions/propose", { method: "POST", token: t, body }),
  approveAction: (t, id) => apiFetch(`/actions/${id}/approve`, { method: "POST", token: t }),
  rejectAction: (t, id) => apiFetch(`/actions/${id}/reject`, { method: "POST", token: t }),

  // Insights
  insightsSummary: (t, from, to) => {
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    const s = p.toString();
    return apiFetch(`/insights/summary${s ? `?${s}` : ""}`, { token: t });
  },
  insightsTopCustomers: (t, from, to) => {
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    const s = p.toString();
    return apiFetch(`/insights/top-customers${s ? `?${s}` : ""}`, { token: t });
  },

  // Tenant
  tenantProfile: (t) => apiFetch("/tenant/profile", { token: t }),
  tenantProfilePatch: (t, body) => apiFetch("/tenant/profile", { method: "PATCH", body, token: t }),
  tenantPlan: (t) => apiFetch("/tenant/plan", { token: t }),
  tenantMe: (t) => apiFetch("/tenants/me", { token: t }),
  tenantMePatch: (t, body) => apiFetch("/tenants/me", { method: "PATCH", body, token: t }),
  plansPublic: () => apiFetch("/plans/public"),

  // Products
  productsList: (t, q) => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    const s = p.toString();
    return apiFetch(`/products${s ? `?${s}` : ''}`, { token: t });
  },
  productsCreate: (t, body) => apiFetch('/products', { method: 'POST', body, token: t }),
  productGet: (t, id) => apiFetch(`/products/${id}`, { token: t }),
  productPatch: (t, id, body) => apiFetch(`/products/${id}`, { method: 'PATCH', body, token: t }),
  productDelete: (t, id) => apiFetch(`/products/${id}`, { method: 'DELETE', token: t }),
  inventoryAdjust: (t, body) => apiFetch('/inventory/adjust', { method: 'POST', body, token: t }),

  // Sales
  salesList: (t, params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) p.set(k, v); });
    const s = p.toString();
    return apiFetch(`/sales${s ? `?${s}` : ''}`, { token: t });
  },
  salesCreate: (t, body) => apiFetch('/sales', { method: 'POST', body, token: t }),
  saleGet: (t, id) => apiFetch(`/sales/${id}`, { token: t }),

  // Expenses
  expensesList: (t, params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
    const s = p.toString();
    return apiFetch(`/expenses${s ? `?${s}` : ''}`, { token: t });
  },
  expensesCreate: (t, body) => apiFetch('/expenses', { method: 'POST', body, token: t }),
  expensePatch: (t, id, body) => apiFetch(`/expenses/${id}`, { method: 'PATCH', body, token: t }),
  expenseDelete: (t, id) => apiFetch(`/expenses/${id}`, { method: 'DELETE', token: t }),
  expenseCategories: (t) => apiFetch('/expenses/categories', { token: t }),
  expenseSummary: (t, params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
    const s = p.toString();
    return apiFetch(`/expenses/summary${s ? `?${s}` : ''}`, { token: t });
  },
  gstRates: () => apiFetch('/meta/gst-rates'),

  // Customers
  customersList: (t, qOrParams) => {
    if (typeof qOrParams === 'string') {
      const s = qOrParams ? `?q=${encodeURIComponent(qOrParams)}` : '';
      return apiFetch(`/customers${s}`, { token: t });
    }
    const p = qOrParams || {};
    const qs = new URLSearchParams();
    if (p.q) qs.set('q', p.q);
    if (p.customer_type) qs.set('customer_type', p.customer_type);
    if (p.active_status) qs.set('active_status', p.active_status);
    if (p.limit) qs.set('limit', String(p.limit));
    if (p.offset != null) qs.set('offset', String(p.offset));
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/customers${s}`, { token: t });
  },
  customerCreate: (t, body) => apiFetch('/customers', { method: 'POST', body, token: t }),
  customerGet: (t, id) => apiFetch(`/customers/${id}`, { token: t }),
  customerPatch: (t, id, body) => apiFetch(`/customers/${id}`, { method: 'PATCH', body, token: t }),

  // Employees
  employeesList: (t, params) => {
    const p = params || {};
    const qs = new URLSearchParams();
    if (p.q) qs.set('q', p.q);
    if (p.active_status) qs.set('active_status', p.active_status);
    if (p.limit) qs.set('limit', String(p.limit));
    if (p.offset != null) qs.set('offset', String(p.offset));
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/employees${s}`, { token: t });
  },
  employeeGet: (t, id) => apiFetch(`/employees/${id}`, { token: t }),
  employeeCreate: (t, body) => apiFetch('/employees', { method: 'POST', body, token: t }),
  employeePatch: (t, id, body) => apiFetch(`/employees/${id}`, { method: 'PATCH', body, token: t }),

  // Students bulk
  studentsBulkTemplateDownload: async (t) => {
    const url = `${BASE}/api/students/bulk-template`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
    if (!res.ok) throw new Error('Failed to download template');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'students-template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  },
  studentsBulkUpload: async (t, file) => {
    const url = `${BASE}/api/students/bulk-upload`;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${t}` }, body: fd });
    const text = await res.text();
    if (!res.ok) {
      const j = safeJson(text);
      throw new ApiError(res.status, j?.detail, formatDetail(j?.detail) || 'Upload failed');
    }
    return safeJson(text) || {};
  },

  // Fee Plans
  feePlansList: (t, params) => {
    const p = params || {};
    const qs = new URLSearchParams();
    if (p.q) qs.set('q', p.q);
    if (p.active_status) qs.set('active_status', p.active_status);
    if (p.limit) qs.set('limit', String(p.limit));
    if (p.offset != null) qs.set('offset', String(p.offset));
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/school/fee-plans${s}`, { token: t });
  },
  feePlanGet: (t, id) => apiFetch(`/school/fee-plans/${id}`, { token: t }),
  feePlanCreate: (t, body) => apiFetch('/school/fee-plans', { method: 'POST', body, token: t }),
  feePlanPatch: (t, id, body) => apiFetch(`/school/fee-plans/${id}`, { method: 'PATCH', body, token: t }),
  feePlanAssign: (t, id, body) => apiFetch(`/school/fee-plans/${id}/assign`, { method: 'POST', body, token: t }),
  feePlanAssignments: (t, id, params) => {
    const p = params || {};
    const qs = new URLSearchParams();
    if (p.limit) qs.set('limit', String(p.limit));
    if (p.offset != null) qs.set('offset', String(p.offset));
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/school/fee-plans/${id}/assignments${s}`, { token: t });
  },
  feePlanAssignmentDeactivate: (t, assignmentId) => apiFetch(`/school/fee-plan-assignments/${assignmentId}/deactivate`, { method: 'POST', token: t }),
  feePlanRunBilling: (t, id, body) => apiFetch(`/school/fee-plans/${id}/run-billing`, { method: 'POST', body, token: t }),
  schoolInsights: (t) => apiFetch('/school/insights', { token: t }),
  collectFeePayment: (t, saleId, body) => apiFetch(`/school/fee-invoices/${saleId}/collect-payment`, { method: 'POST', body, token: t }),

  // Billing Logs
  billingLogsList: (t, params) => {
    const p = params || {};
    const qs = new URLSearchParams();
    if (p.fee_plan_id) qs.set('fee_plan_id', p.fee_plan_id);
    if (p.limit) qs.set('limit', String(p.limit));
    if (p.offset != null) qs.set('offset', String(p.offset));
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/school/billing-logs${s}`, { token: t });
  },
  billingLogGet: (t, id) => apiFetch(`/school/billing-logs/${id}`, { token: t }),

  // Team & Users
  tenantUsers: (t) => apiFetch('/tenants/users', { token: t }),
  tenantRemoveUser: (t, id) => apiFetch(`/tenants/users/${id}`, { method: 'DELETE', token: t }),
  invitesList: (t) => apiFetch('/invites', { token: t }),
  invitesCreate: (t, role) => apiFetch('/invites', { method: 'POST', body: { role }, token: t }),
  invitesDelete: (t, id) => apiFetch(`/invites/${id}`, { method: 'DELETE', token: t }),

  // Branches
  branchesList: (t) => apiFetch('/branches', { token: t }),
  branchCreate: (t, body) => apiFetch('/branches', { method: 'POST', body, token: t }),

  // Onboarding
  onboardingIndustries: () => apiFetch('/onboarding/industries'),
  onboardingComplete: (t, industry) => apiFetch('/onboarding/complete', { method: 'POST', body: { industry }, token: t }),

  // Billing
  billingSimulateUpgrade: (t, plan) => apiFetch('/billing/simulate-upgrade', { method: 'POST', body: { plan }, token: t }),

  // Auth extras
  changePassword: (t, body) => apiFetch('/auth/change-password', { method: 'POST', body, token: t }),

  // Today Engine
  aiToday: (t) => apiFetch('/ai/today', { token: t }),
  aiTodayRefresh: (t) => apiFetch('/ai/today/refresh', { method: 'POST', token: t, timeoutMs: 180000 }),

  // Inventory Intelligence
  inventoryIntelligence: (t) => apiFetch('/inventory/intelligence', { token: t }),
  inventoryIntelligenceRefresh: (t) => apiFetch('/inventory/intelligence/refresh', { method: 'POST', token: t, timeoutMs: 180000 }),

  // Sales Strategy
  salesStrategy: (t) => apiFetch('/sales-strategy', { token: t }),
  salesStrategyRefresh: (t) => apiFetch('/sales-strategy/refresh', { method: 'POST', token: t, timeoutMs: 180000 }),

  // AI Command (Phase 13 — Add via AI)
  aiCommand: (t, body) => apiFetch('/ai/command', { method: 'POST', body, token: t, timeoutMs: 60000 }),
  aiCommandExecute: (t, body) => apiFetch('/ai/command/execute', { method: 'POST', body, token: t, timeoutMs: 60000 }),

  // Smart ERP Engine (Phase 17)
  erpTrialBalance: (t, period) => apiFetch(`/erp/trial-balance${period ? `?period=${period}` : ''}`, { token: t }),
  erpPnL: (t, period) => apiFetch(`/erp/pnl${period ? `?period=${period}` : ''}`, { token: t }),
  erpBalanceSheet: (t) => apiFetch('/erp/balance-sheet', { token: t }),

  // Professional Accounting Suite (Phase 19)
  erpJournalList: (t) => apiFetch('/erp/journal', { token: t }),
  erpJournalCreate: (t, body) => apiFetch('/erp/journal', { method: 'POST', body, token: t }),
  erpJournalGet: (t, id) => apiFetch(`/erp/journal/${id}`, { token: t }),
  erpJournalDelete: (t, id) => apiFetch(`/erp/journal/${id}`, { method: 'DELETE', token: t }),
  erpLedgerAccounts: (t) => apiFetch('/erp/ledger/accounts', { token: t }),
  erpLedger: (t, params) => {
    const p = new URLSearchParams();
    if (params.account_name) p.set('account_name', params.account_name);
    if (params.from) p.set('from', params.from);
    if (params.to) p.set('to', params.to);
    if (params.limit) p.set('limit', String(params.limit));
    if (params.offset) p.set('offset', String(params.offset));
    const s = p.toString();
    return apiFetch(`/erp/ledger${s ? `?${s}` : ''}`, { token: t });
  },
  erpAging: (t, params) => {
    const p = new URLSearchParams();
    if (params.type) p.set('type', params.type);
    if (params.as_of) p.set('as_of', params.as_of);
    const s = p.toString();
    return apiFetch(`/erp/aging${s ? `?${s}` : ''}`, { token: t });
  },
  erpGST: (t, params) => {
    const p = new URLSearchParams();
    if (params.month) p.set('month', params.month);
    return apiFetch(`/erp/gst?${p.toString()}`, { token: t });
  },
  erpBankStatements: (t) => apiFetch('/erp/bank/statements', { token: t }),
  erpBankStatement: (t, id) => apiFetch(`/erp/bank/statements/${id}`, { token: t }),
  erpBankMatch: (t, statementId) => apiFetch('/erp/bank/match', { method: 'POST', body: { statement_id: statementId }, token: t }),
  erpBankManualMatch: (t, body) => apiFetch('/erp/bank/manual-match', { method: 'POST', body, token: t }),

  // AI Boardroom (Phase 16A)
  boardroomAsk: (t, body) => apiFetch('/boardroom/ask', { method: 'POST', body, token: t, timeoutMs: 90000 }),
  boardroomThreads: (t) => apiFetch('/boardroom/threads', { token: t }),
  boardroomThread: (t, id) => apiFetch(`/boardroom/threads/${id}`, { token: t }),
  boardroomBriefings: (t) => apiFetch('/boardroom/briefings', { token: t }),
  boardroomBriefing: (t, id) => apiFetch(`/boardroom/briefings/${id}`, { token: t }),
  boardroomBriefingsRun: (t) => apiFetch('/boardroom/briefings/run', { method: 'POST', token: t, timeoutMs: 180000 }),

  // GCC Region & Tax (Phase 20)
  regionGet: (t) => apiFetch('/tenants/me/region', { token: t }),
  regionPatch: (t, body) => apiFetch('/tenants/me/region', { method: 'PATCH', body, token: t }),
  regionsPresets: () => apiFetch('/regions/presets'),
  publicTenantBySlug: (slug) => apiFetch(`/public/tenants/by-slug/${slug}`),
  erpVAT: (t, params) => {
    const p = new URLSearchParams();
    if (params?.month) p.set('month', params.month);
    return apiFetch(`/erp/vat?${p.toString()}`, { token: t });
  },

  // Multi-Branch Ledger (Phase 17)
  multiBranchConsolidated: (t, period) => apiFetch(`/multi-branch/consolidated${period ? `?period=${period}` : ''}`, { token: t }),

  // Marketing Addon (Phase 6)
  marketingAddonStatus: (t) => apiFetch('/marketing/addon/status', { token: t }),
  activateMarketingAddon: (t) => apiFetch('/marketing/addon/activate', { method: 'POST', token: t }),
  deactivateMarketingAddon: (t) => apiFetch('/marketing/addon/deactivate', { method: 'POST', token: t }),
  buyMarketingCredits: (t, body) => apiFetch('/marketing/addon/buy-credits', { method: 'POST', body, token: t }),
  generateMarketingDesign: (t, body) => apiFetch('/marketing/generate', { method: 'POST', body, token: t, timeoutMs: 180000 }),
  listMarketingAssets: (t) => apiFetch('/marketing/assets', { token: t }),
  getMarketingAsset: (t, id) => apiFetch(`/marketing/assets/${id}`, { token: t }),
  deleteMarketingAsset: (t, id) => apiFetch(`/marketing/assets/${id}`, { method: 'DELETE', token: t }),
  socialConnect: (t, body) => apiFetch('/marketing/social/connect', { method: 'POST', body, token: t }),
  socialDisconnect: (t, body) => apiFetch('/marketing/social/disconnect', { method: 'POST', body, token: t }),
  getSocialStatus: (t) => apiFetch('/marketing/social/status', { token: t }),
  scheduleAsset: (t, id, body) => apiFetch(`/marketing/assets/${id}/schedule`, { method: 'POST', body, token: t }),
  listScheduled: (t, status) => apiFetch(`/marketing/scheduled${status ? `?status=${status}` : ''}`, { token: t }),
  cancelScheduled: (t, id) => apiFetch(`/marketing/scheduled/${id}/cancel`, { method: 'POST', token: t }),

  // Brand Logo (Phase 21 — Tenant White-Labeling)
  brandLogoGet: (t) => apiFetch('/tenants/me/brand-logo', { token: t }),

  // Purchase Orders (Phase 21)
  purchaseOrdersList: (t, params = {}) => {
    const q = new URLSearchParams();
    if (params.page_size) q.set('page_size', params.page_size);
    if (params.offset) q.set('offset', params.offset);
    return apiFetch(`/purchase-orders?${q}`, { token: t });
  },
  purchaseOrdersCreate: (t, body) => apiFetch('/purchase-orders', { method: 'POST', body, token: t }),
  // Vouchers (Phase 22)
  voucherPaymentCreate: (t, body) => apiFetch('/erp/vouchers/payment', { method: 'POST', body, token: t }),
  voucherReceiptCreate: (t, body) => apiFetch('/erp/vouchers/receipt', { method: 'POST', body, token: t }),
  voucherContraCreate: (t, body) => apiFetch('/erp/vouchers/contra', { method: 'POST', body, token: t }),
  voucherList: (t, params = {}) => {
    const q = new URLSearchParams();
    if (params.type) q.set('type', params.type);
    if (params.party_id) q.set('party_id', params.party_id);
    if (params.from_date) q.set('from_date', params.from_date);
    if (params.to_date) q.set('to_date', params.to_date);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return apiFetch(`/erp/vouchers?${q}`, { token: t });
  },
  voucherGet: (t, id) => apiFetch(`/erp/vouchers/${id}`, { token: t }),
  bankAccountsList: (t) => apiFetch('/erp/bank-accounts', { token: t }),
  // Credit Notes & Debit Notes (Phase 22 Step 2)
  creditNoteCreate: (t, body) => apiFetch('/erp/credit-notes', { method: 'POST', body, token: t }),
  creditNoteList: (t, params = {}) => {
    const q = new URLSearchParams();
    if (params.sale_id) q.set('sale_id', params.sale_id);
    if (params.customer_id) q.set('customer_id', params.customer_id);
    if (params.from_date) q.set('from_date', params.from_date);
    if (params.to_date) q.set('to_date', params.to_date);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return apiFetch(`/erp/credit-notes?${q}`, { token: t });
  },
  creditNoteGet: (t, id) => apiFetch(`/erp/credit-notes/${id}`, { token: t }),
  debitNoteCreate: (t, body) => apiFetch('/erp/debit-notes', { method: 'POST', body, token: t }),
  debitNoteList: (t, params = {}) => {
    const q = new URLSearchParams();
    if (params.po_id) q.set('po_id', params.po_id);
    if (params.supplier_name) q.set('supplier_name', params.supplier_name);
    if (params.from_date) q.set('from_date', params.from_date);
    if (params.to_date) q.set('to_date', params.to_date);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return apiFetch(`/erp/debit-notes?${q}`, { token: t });
  },
  debitNoteGet: (t, id) => apiFetch(`/erp/debit-notes/${id}`, { token: t }),
  purchaseOrderGet: (t, id) => apiFetch(`/purchase-orders/${id}`, { token: t }),

  // Outstanding (Phase 22 Step 3)
  outstandingReceivables: (t) => apiFetch('/erp/outstanding/receivables', { token: t }),
  outstandingReceivableDetail: (t, customerId) => apiFetch(`/erp/outstanding/receivables/${customerId}`, { token: t }),
  outstandingPayables: (t) => apiFetch('/erp/outstanding/payables', { token: t }),
  outstandingPayableDetail: (t, vendorKey) => apiFetch(`/erp/outstanding/payables/${encodeURIComponent(vendorKey)}`, { token: t }),
  outstandingWhatsappPreview: (t, body) => apiFetch('/erp/outstanding/whatsapp-preview', { method: 'POST', body, token: t }),

  // GSTR-1 (Phase 22 Step 4)
  gstr1Summary: (t, month) => apiFetch(`/erp/gstr1?month=${month}`, { token: t }),

  bankAccountCreate: (t, body) => apiFetch('/erp/bank-accounts', { method: 'POST', body, token: t }),

  brandLogoDelete: (t) => apiFetch('/tenants/me/brand-logo', { method: 'DELETE', token: t }),
  brandLogoUpload: async (token, file) => {
    const url = `${BASE}/api/tenants/me/brand-logo`;
    const form = new FormData();
    form.append('logo', file);
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const text = await res.text();
    const data = text ? safeJson(text) : null;
    if (!res.ok) {
      const detail = (data && typeof data === 'object' && data.detail) || text || res.statusText;
      throw new ApiError(res.status, data, formatDetail(detail));
    }
    return data;
  },
};
